/**
 * MarketingConsentField — the newsletter opt-in, separate from Terms/Privacy.
 *
 * Why it is its own control and not a third line in the agreement list:
 * accepting Terms and Privacy is a precondition of using the product, and
 * bundling a newsletter into that is not consent. Both signup paths used to
 * enroll people into the marketing series on the strength of that acceptance —
 * Bitrix24 Marketplace moderation flagged it (2026-08-12), and the web path was
 * doing exactly the same thing.
 *
 * Consequences pinned here:
 *  - UNTICKED by default. A pre-ticked box is not an opt-in.
 *  - Renders NOTHING when there is no address. There is nothing to subscribe,
 *    and an input asking for one would need e-mail verification first (an
 *    unverified address routes someone else's reports to a stranger).
 *  - Shows the address rather than asking for it, so the person can see exactly
 *    where mail would go before agreeing to any.
 *  - Says what the mail IS (#2126). The series walks a new customer through
 *    setup and adapts to what they have done (privacy B.5: setup guidance,
 *    feature introductions, product news). Worded as «newsletter» and «product
 *    news», under two legal boxes, it read as a request to send advertising.
 */
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { isPlaceholderEmail } from '@aica/core';
import { Checkbox } from '@/components/ui/checkbox';

interface MarketingConsentFieldProps {
  /** The address mail would go to. Absent → the control does not render. */
  email: string | null | undefined;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Ties the checkbox to its label; must be unique on the page. */
  id?: string;
}

export function MarketingConsentField({
  email,
  checked,
  onCheckedChange,
  id = 'marketing-consent',
}: MarketingConsentFieldProps) {
  const { t } = useTranslation(['onboarding']);

  // A placeholder is not an address. A portal user created under the minimal
  // Bitrix24 scope carries `user-<uuid>@no-mailbox.invalid`; showing it and
  // offering to subscribe it would be an offer we cannot keep.
  if (!email || isPlaceholderEmail(email)) return null;

  return (
    <div className="rounded-card border border-border bg-bg-surface p-4 text-left">
      {/* The benefit first, the ask second: what this address is FOR is the
          digest of what is happening in their account — a service message they
          get regardless. The newsletter is the optional extra below it. */}
      <p className="flex items-start gap-2 text-sm text-fg">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
        <span>
          {t('marketing.reportsGoTo')}{' '}
          <span className="font-medium break-all">{email}</span>
        </span>
      </p>

      {/* The opt-in reads as help on offer, not as a third agreement: the
          label says what the person gets, the hint says what arrives, how
          often, and how to stop. It sits below a divider instead of inside a
          highlighted box, and carries no «Optional» badge: an unticked box
          outside the legal list already says so, and repeating it twice read
          as the defensiveness of someone pushing a subscription (#2126).

          What did NOT change, and must not: unticked by default, outside
          `canContinue`, and worded as what it is. The hint names the plans
          email too (series e8): softer copy is fine, a consent that leaves out
          part of what it covers is not. It promises no per-step nudges either —
          a late subscriber (portal link, Settings, an existing organization)
          has no milestone state, and SendPulse skips those
          (`subscribeToNewsletter`). */}
      <div className="mt-3 border-t border-border pt-2">
        <label
          htmlFor={id}
          className="flex cursor-pointer items-start gap-3 rounded-card p-2 text-left transition-colors hover:bg-bg-elevated"
        >
          <Checkbox
            id={id}
            checked={checked}
            onCheckedChange={(next) => onCheckedChange(next === true)}
            className="mt-0.5"
          />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-fg">
              {t('marketing.optInLabel')}
            </span>
            <span className="mt-1 block text-xs text-fg-muted">
              {t('marketing.optInHint')}
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}
